import React, { useEffect, useState } from 'react';
import { EmailSender, getSenders, createSender, updateSender } from '../../services/emailApi';

const providerDisplayMap: Record<number, string> = { 1: 'SMTP', 2: 'SendGrid', 3: 'SES' };
const providerValueFromLabel: Record<string, number> = { SMTP: 1, SendGrid: 2, SES: 3 };

const EmailSendersPage: React.FC = () => {
  const [senders, setSenders] = useState<EmailSender[]>([]);
  const [form, setForm] = useState<EmailSender>({ emailAddress: '', providerType: 1, description: '', isActive: true });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSenders();
      setSenders(Array.isArray(data) ? data : (data?.senders || []));
    } catch (e: any) {
      setError(e?.message || 'Failed to load senders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    // Prevent duplicate email sender
    const emailExists = senders.some(s => s.emailAddress.toLowerCase() === form.emailAddress.trim().toLowerCase() && (!editingId || String(s.id) !== String(editingId)));
    if (emailExists) {
      setError('A sender with this email address already exists.');
      return;
    }
    try {
      if (editingId) {
        await updateSender(editingId, form);
      } else {
        await createSender(form);
      }
      setForm({ emailAddress: '', providerType: 1, description: '', isActive: true });
      setEditingId(null);
      await load();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setError('A sender with this email address already exists.');
      } else {
        const msg = err?.response?.data?.message || err?.message || 'Failed to save sender';
        setError(typeof msg === 'string' ? msg : 'Server error');
      }
    }
  };

  const startEdit = (s: EmailSender) => {
    setEditingId(String(s.id ?? ''));
    setForm({
      emailAddress: s.emailAddress,
      providerType: typeof s.providerType === 'number' ? s.providerType : providerValueFromLabel[String(s.providerType)] || 1,
      description: s.description || '',
      isActive: !!s.isActive,
      id: s.id,
    } as EmailSender);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ emailAddress: '', providerType: 1, description: '', isActive: true });
    setError(null);
  };

  return (
    <div className="p-6 bg-background min-h-screen">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-primary">Email Senders</h1>
      </div>

  <form onSubmit={submit} className="bg-white rounded shadow p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <input
          required
          placeholder="Sender Email Address"
          value={form.emailAddress}
          onChange={(e) => setForm({ ...form, emailAddress: e.target.value })}
          className="border rounded px-3 py-2 text-sm"
        />
        <select
          value={typeof form.providerType === 'number' ? form.providerType : providerValueFromLabel[String(form.providerType)]}
          onChange={(e) => setForm({ ...form, providerType: Number(e.target.value) })}
          className="border rounded px-3 py-2 text-sm"
        >
          <option value={1}>SMTP</option>
          <option value={2}>SendGrid</option>
          <option value={3}>SES</option>
        </select>
        <input
          placeholder="Description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="border rounded px-3 py-2 text-sm"
        />
        <div className="flex items-center gap-2">
          <input
            id="isActive"
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
          />
          <label htmlFor="isActive">Active</label>
        </div>
        <div className="flex items-center gap-2">
          <button type="submit" className="px-4 py-2 bg-primary text-white rounded">{editingId ? 'Update' : 'Add'}</button>
          {editingId && (
            <button type="button" onClick={cancelEdit} className="px-4 py-2 border rounded">Cancel</button>
          )}
        </div>
      </form>

      {error && <div className="mb-3 text-red-600 text-sm">{error}</div>}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="bg-white rounded shadow overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-left p-2">Email</th>
                <th className="text-left p-2">Provider</th>
                <th className="text-left p-2">Description</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {senders.map(s => (
                <tr key={s.id || s.emailAddress} className="border-t">
                  <td className="p-2">{s.emailAddress}</td>
                  <td className="p-2">{typeof s.providerType === 'number' ? providerDisplayMap[s.providerType] || s.providerType : s.providerType}</td>
                  <td className="p-2">{s.description || '-'}</td>
                  <td className="p-2">{s.isActive ? 'Active' : 'Inactive'}</td>
                  <td className="p-2">
                    <button type="button" onClick={() => startEdit(s)} className="text-primary hover:underline">Edit</button>
                  </td>
                </tr>
              ))}
              {senders.length === 0 && (
                <tr>
                  <td className="p-4" colSpan={5}>No senders</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EmailSendersPage;
